/**
 * Parses UI lines like `path=$.ReferenceNumbersList; key=BL#` or `path=$.x; keys=SID, BOL`
 * into the API shape `Identifier`: { keys: string[], path: string } (camelCase JSON).
 */
function pathKeysLineToIdentifierDto(line) {
    const trimmed = String(line ?? '').trim();
    if (!trimmed) return null;
    let path = '';
    const keys = [];
    const segments = trimmed.split(';');
    for (let seg of segments) {
        seg = seg.trim();
        if (!seg) continue;
        const pathMatch = seg.match(/^path\s*=\s*(.+)$/i);
        if (pathMatch) {
            path = pathMatch[1].trim();
            continue;
        }
        const keyMatch = seg.match(/^key\s*=\s*(.+)$/i);
        if (keyMatch) {
            keys.push(keyMatch[1].trim());
            continue;
        }
        const keysMatch = seg.match(/^keys\s*=\s*(.+)$/i);
        if (keysMatch) {
            for (const k of keysMatch[1].split(',')) {
                const t = k.trim();
                if (t) keys.push(t);
            }
            continue;
        }
    }
    if (!path || keys.length === 0) return null;
    return { keys, path };
}

/**
 * Coerces one messageTypes group `identifiers` entry to `Identifier` DTO, or null if invalid.
 * Accepts path/key lines, objects from the API (camelCase or PascalCase), or JSON strings of those objects.
 */
export function normalizeApiIdentifierEntry(item) {
    if (item == null) return null;
    if (typeof item === 'string') {
        const t = item.trim();
        if (!t) return null;
        try {
            const parsed = JSON.parse(t);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                return normalizeApiIdentifierEntry(parsed);
            }
        } catch {
            /* fall through */
        }
        return pathKeysLineToIdentifierDto(t);
    }
    if (typeof item === 'object' && !Array.isArray(item)) {
        const path = item.path ?? item.Path;
        let keys = item.keys ?? item.Keys;
        if (keys != null && !Array.isArray(keys)) {
            keys = [String(keys)];
        }
        if (path != null && String(path).trim() !== '' && Array.isArray(keys) && keys.length > 0) {
            return { keys: keys.map(String), path: String(path).trim() };
        }
    }
    return null;
}

function normalizeIdentifiersForPut(identifiers) {
    if (identifiers == null) return [];
    const arr = Array.isArray(identifiers) ? identifiers : [identifiers];
    const out = [];
    for (const item of arr) {
        const n = normalizeApiIdentifierEntry(item);
        if (n != null) out.push(n);
    }
    return out;
}

/**
 * Inverse of parsing a legacy bracket predicate: rebuilds one selector string for API `List<string> Selectors`.
 * Accepts camelCase or PascalCase property names (JSON from .NET).
 */
function legacySelectorDtoToExpression(obj) {
    if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return null;
    const identifierList = obj.identifierList ?? obj.IdentifierList;
    const key = obj.key ?? obj.Key;
    const operator = obj.operator ?? obj.Operator;
    const value = obj.value ?? obj.Value;
    if (
        identifierList == null ||
        String(identifierList).trim() === '' ||
        key == null ||
        operator == null ||
        String(operator).trim() === ''
    ) {
        return null;
    }
    const keyLiteral = String(key).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    let rhs;
    if (value === null) rhs = 'null';
    else if (typeof value === 'boolean') rhs = value ? 'true' : 'false';
    else if (typeof value === 'number') rhs = `'${String(value)}'`;
    else if (typeof value === 'string') {
        rhs = `'${String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
    } else if (value === undefined) return null;
    else rhs = JSON.stringify(value);
    const op = String(operator).trim();
    const idListName = String(identifierList).trim();
    /** `ReferenceNumbers` is a string-keyed map in payloads; other lists use Key/Value rows. */
    if (idListName === 'ReferenceNumbers') {
        return `${identifierList}['${keyLiteral}']${op}${rhs}`;
    }
    return `${identifierList}[Key='${keyLiteral}'].Value${op}${rhs}`;
}

/**
 * Coerces messageTypes group `selectors` to `List<string>` predicate lines, never objects.
 * Separate entries stay separate. The runtime ORs array entries; joining them with `and` would change the agreement.
 */
export function normalizeSelectorsForPut(selectors) {
    if (selectors == null) return [];
    const arr = Array.isArray(selectors) ? selectors : [selectors];
    const out = [];
    for (const item of arr) {
        if (item == null) continue;
        if (typeof item === 'string') {
            const t = item.trim();
            if (t) out.push(t);
            continue;
        }
        // Selector lines can be JSON.parse'd to booleans/numbers in the UI; keep them as string entries.
        if (typeof item === 'boolean') {
            out.push(item ? 'true' : 'false');
            continue;
        }
        if (typeof item === 'number') {
            if (Number.isNaN(item)) continue;
            out.push(String(item));
            continue;
        }
        if (Array.isArray(item)) {
            try {
                out.push(JSON.stringify(item));
            } catch {
                /* skip */
            }
            continue;
        }
        if (typeof item === 'object' && !Array.isArray(item)) {
            const expr = legacySelectorDtoToExpression(item);
            if (expr != null) {
                out.push(expr);
                continue;
            }
            try {
                const s = JSON.stringify(item);
                if (s && s !== '{}') out.push(s);
            } catch {
                /* skip */
            }
        }
    }
    return out;
}

export function normalizeMessageTypesForPut(messageTypes) {
    if (!messageTypes || typeof messageTypes !== 'object' || Array.isArray(messageTypes)) {
        return {};
    }
    /** @type {Record<string, Array<Record<string, unknown>>>} */
    const out = {};
    for (const [eventKey, groups] of Object.entries(messageTypes)) {
        if (!Array.isArray(groups)) continue;
        out[eventKey] = groups.map((g) => {
            if (!g || typeof g !== 'object') {
                return {
                    groupName: '',
                    selectors: [],
                    identifiers: [],
                    respondedStatus: 'Pending'
                };
            }
            const row = /** @type {Record<string, unknown>} */ (g);
            const rawIdentifiers = Array.isArray(row.identifiers)
                ? row.identifiers
                : row.identifiers == null
                    ? []
                    : [row.identifiers];
            const base = {
                // Keep null group names as null. The provider status update matches on
                // (eventType, groupName); coercing null to '' prevents the status change.
                groupName: row.groupName == null ? null : String(row.groupName),
                selectors: normalizeSelectorsForPut(row.selectors),
                identifiers: normalizeIdentifiersForPut(rawIdentifiers)
            };
            const entry = { ...base };
            if (row.respondedStatus != null && row.respondedStatus !== '') {
                entry.respondedStatus = row.respondedStatus;
            }
            return entry;
        });
    }
    return out;
}

/**
 * V2 API may omit this field; ensures UI state and request DTOs use only `true` or `false`.
 * Non-boolean values (e.g. null) become `false`.
 * @param {unknown} value
 * @returns {boolean}
 */
export function normalizeDeliverToOtherParties(value) {
    if (value === true) return true;
    if (value === false) return false;
    return false;
}
